import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { SkillSelectionSource } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import type { StreamResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { getPageCreateVisualBlockCounts } from '../../common/utils/get-page-create-visual-block-counts';
import { constructCitedProductList } from './constructCitedProductList';
import { constructSourceProducts } from './constructSourceProducts';
import { containsServiceDeskSource } from './containsServiceDeskSource';
import { extractConnectorInfo } from './extractConnectorInfo';
import { getInvokedFrom } from './getInvokedFrom';
import type { SkillAttributes } from './SkillAttributes';
import type { StreamMeta } from './StreamMeta';

/**
 * Derives non-UGC authority attributes from the response's authority_ui_metadata.
 * Only counts, booleans, and the coarse level/confidence are emitted — never
 * source titles, expert names, URLs, or reasoning text. Gated on the
 * authoritative-answer feature flag.
 */
const extractAuthorityMeta = (
	response: StreamResponse,
): Pick<
	StreamMeta,
	| 'hasAuthoritativeSources'
	| 'authorityLevel'
	| 'isHighlyAuthoritative'
	| 'overallConfidence'
	| 'authoritativeSourceCount'
	| 'authoritativeExpertCount'
> => {
	if (!fg('rovo_authoritative_answer_attribution')) {
		return { hasAuthoritativeSources: false };
	}

	const message = response.message?.message;
	// Top level for streaming responses; nested under message_metadata for history.
	const authorityUiMetadata =
		message?.authority_ui_metadata ?? message?.message_metadata?.authority_ui_metadata;

	if (!authorityUiMetadata) {
		return { hasAuthoritativeSources: false };
	}

	return {
		hasAuthoritativeSources: true,
		authorityLevel: authorityUiMetadata.authorityLevel,
		isHighlyAuthoritative:
			authorityUiMetadata.isHighlyAuthoritative ?? authorityUiMetadata.highlyAuthoritative ?? false,
		overallConfidence: authorityUiMetadata.overallConfidence,
		authoritativeSourceCount: authorityUiMetadata.sources?.length ?? 0,
		authoritativeExpertCount: authorityUiMetadata.domainExperts?.length ?? 0,
	};
};

export const extractStreamMeta = (
	response: StreamResponse,
	skillSelectionSource?: SkillSelectionSource,
	/**
	 * Whether to include messageDynamicUIData (dynamic UI action metadata).
	 * Pass the result of expValEquals('cc-maui-experiment', 'isEnabled', true) from the call site
	 * to keep experiment checks out of this utility function.
	 */
	includeDynamicUIData?: boolean,
): StreamMeta => {
	const sources = response.message?.message?.sources;
	const pluginInvocations = response.message?.message?.plugin_invocations;
	const minionOutputs = response.message?.message?.minion_outputs;
	const actions = response.message?.message?.actions;
	const humanMessageId = response.message?.human_message?.id.toString();
	const pluginsInvoked: string[] = [];
	const skillsInvoked: SkillAttributes[] = [];
	let usesJsm = false;

	pluginInvocations?.forEach((pi) => {
		if (pi.plugin_name) {
			pluginsInvoked.push(pi.plugin_name);
		}
		if (pi.plugin_output && containsServiceDeskSource(pi.plugin_output)) {
			usesJsm = true;
		}
	});

	const flatMinionOutputs = minionOutputs?.flat() ?? [];

	const executedStratusSkill =
		flatMinionOutputs.some((output) => output.skill_type === 'STRATUS') ?? false;

	flatMinionOutputs.forEach((output) => {
		pluginsInvoked.push(output.minion_id);
		const isExplicit = output.directly_invoked;
		skillsInvoked.push({
			minionId: output.minion_id,
			skillName: output.skill_alias,
			skillType: output.skill_type,
			invocationType: isExplicit ? 'explicit' : 'implicit',
			internalTools:
				output.internal_tools?.map((tool) => ({
					name: tool.name,
					isConsequential: tool.is_consequential,
				})) ?? [],
			skillSelectionSource: isExplicit ? skillSelectionSource : undefined,
		});

		if (output.minion_output && containsServiceDeskSource(output.minion_output)) {
			usesJsm = true;
		}
	});

	const requestId =
		response.metadata?.request_id ?? response.message?.message?.message_metadata?.requestId;
	return {
		...(fg('aifc_request_id_analytics') && requestId ? { requestId } : {}),
		humanMessageId,
		citedProductList: constructCitedProductList(sources),
		connectorInfo: fg('rovo_chat_connector_info_analytics')
			? extractConnectorInfo(sources)
			: undefined,
		sourceProducts: constructSourceProducts(sources),
		hasSources: !!sources?.length,
		...extractAuthorityMeta(response),
		pluginsInvoked,
		skillsInvoked,
		executedStratusSkill,
		messageActions: actions?.map((action) => action.key),
		messageDynamicUIData: includeDynamicUIData
			? actions?.map((action) => ({
					dynamicUiType: (action.data as { contentType?: string } | undefined)?.contentType,
					dynamicUiSubtype: (action.data as { dynamicUiSubtype?: string } | undefined)
						?.dynamicUiSubtype,
				}))
			: undefined,
		invokedFrom: getInvokedFrom({
			messageActionKey: actions?.[0]?.key,
			messageActionData: actions?.[0]?.data,
		}),
		visualBlockCounts: getPageCreateVisualBlockCounts(actions),
		responseSources: sources?.map((x) => x.ari).filter(Boolean),
		usesJsm,
		scenarioId: response.message?.message?.scenario?.id,
		interactionModeExecuted: response.message?.message?.message_metadata?.interaction_mode_executed,
		agentExternalConfigReference: fg('add_mci_kr_attributes_to_rovo_chat_analytics')
			? (response.message?.message?.author?.external_config_reference ??
				response.message?.message?.author?.named_id)
			: undefined,
	};
};
